<template>
  <article class="demo">
    <div class="demo__top">
      <div class="grid grid--container">
        <div class="demo__content">
          <VText class="demo__title" variant="h1" style="margin-bottom: 1rem"
            >Request a personalized demo of Campsite.bio</VText
          >
          <VText gutter-bottom
            >We look forward to showing you how our platform can help you build
            a better link in bio tool and beyond.</VText
          >

          <VText component="h2" variant="h3" gutter-bottom
            >What can I expect?</VText
          >

          <ul>
            <li>
              Discuss your link in bio goals and what you need from our tool to
              succeed
            </li>
            <li>
              A live product demo aligning campsite.bio to your business needs
            </li>
            <li>No commitment required</li>
          </ul>
        </div>
        <Card class="demo__form" :hideStaticShadow="false">
          <CardText>
            <VText component="h2" variant="h4" style="margin-bottom: 0.5rem"
              >Request a demo</VText
            >
            <VText gutter-bottom>Let us show you around our Pro+ plan.</VText>
            <DemoForm />
          </CardText>
        </Card>
      </div>
    </div>

    <SeenOn />

    <FeatureSection
      title="Unlock our best with the Pro+ subscription"
      bg-color="gray100"
    >
      <VText slot="text"
        >Get all the features you need in a link in bio app with our Pro+ or
        organization subscriptions.</VText
      >
      <Button slot="cta" to="/pricing">Compare features</Button>

      <FeatureCard
        span="2"
        title="Reporting tools"
        bg-color="secondary"
        color="secondary"
      >
        <LineChartIcon slot="icon" />
        <VText
          >We're constantly gathering data on what is happening on your
          profiles. We dissect and present your data in five different reports
          to help you gain insights. We make it easy to view your link in bio
          performance and make improvements.
        </VText>
        <img
          slot="img"
          src="~/assets/images/use-cases/smm/leaderboard-report.png"
          loading="lazy"
          alt="Leaderboard report"
        />
      </FeatureCard>
      <FeatureCard title="Six profiles">
        <AccountMultipleIcon slot="icon" />
        <VText
          >Get all of your brands/clients added with built-in support for six
          profiles. Need more? Modify your plan to support more for an
          additional cost.
        </VText>
      </FeatureCard>
      <FeatureCard title="Four collaborators">
        <PersonAddIcon slot="icon" />
        <VText
          >Add your teammates or clients to help manage your profiles. Four are
          included, with the ability to add more for an additional cost.
        </VText>
      </FeatureCard>
      <FeatureCard title="Premium link types">
        <GridIcon slot="icon" />
        <VText
          >Unlock our best link types with multiple carousels, opt-in forms,
          feed links, forms (Typeform and Jotform), image grids and so much
          more.</VText
        >
      </FeatureCard>
      <FeatureCard title="Automation">
        <SettingsIcon slot="icon" />
        <VText
          >Keep your profile fresh with our automated link types, like the feed
          and image grid. They'll pull in your newest content, automatically.
        </VText>
      </FeatureCard>
      <FeatureCard title="Reports">
        <FileDownloadIcon slot="icon" />
        <VText
          >Access five different reports that slice and dice your profile data
          so you can make improvements quickly and report back performance with
          ease.
        </VText>
      </FeatureCard>
      <FeatureCard title="Filtering & Exports">
        <FilterIcon slot="icon" />
        <VText
          >Filter down our analytics by country, device, referrer, or campaign.
          You can even download your analytics data to dive deeper into it.
        </VText>
      </FeatureCard>
    </FeatureSection>

    <RaisedCtaSection
      title="Simplify your link in bio management today"
      text="All of your accounts in one easy to manage place."
    >
      <Button href="https://app.campsite.bio/create-account" size="large"
        >Get started</Button
      >
    </RaisedCtaSection>
  </article>
</template>

<script>
import {
  VText,
  Card,
  CardText,
  Button,
  LineChartIcon,
  AccountMultipleIcon,
  PersonAddIcon,
  GridIcon,
  SettingsIcon,
  FilterIcon,
  FileDownloadIcon,
} from '@campsite-bio/component-lib';

import { generateMeta } from '~/utils/generate-meta';
import DemoForm from '@/components/forms/demo-form';
import SeenOn from '@/components/seen-on-analytics';
import FeatureCard from '@/components/features/feature-card';
import FeatureSection from '@/components/features/feature-section';
import RaisedCtaSection from '@/components/sections/raised-cta-section';

export default {
  head() {
    return generateMeta({
      title: 'Demo Request - Campsite.bio',
      description:
        'Campsite.bio provides a robust link in bio tool for small businesses, agencies, and social media managers. Sign up for a demo of Pro+.',
    });
  },

  components: {
    VText,
    DemoForm,
    Card,
    CardText,
    SeenOn,
    FeatureCard,
    FeatureSection,
    Button,
    LineChartIcon,
    AccountMultipleIcon,
    PersonAddIcon,
    GridIcon,
    SettingsIcon,
    FilterIcon,
    FileDownloadIcon,
    RaisedCtaSection,
  },

  setup() {
    return {};
  },
};
</script>

<style lang="scss" scoped>
.demo {
  &__top {
    background: #799573;
    padding-bottom: 3rem;
    padding-top: 3rem;

    @media (min-width: 992px) {
      padding-bottom: 4rem;
      padding-top: 5rem;
    }
  }

  .grid {
    @media (min-width: 992px) {
      display: grid;
      gap: 3rem;
      grid-template-columns: 0.75fr 1fr;
    }
  }

  &__content {
    font-size: 1.125rem;

    @media (min-width: 768px) {
      font-size: 1.25rem;
    }

    li {
      margin-bottom: 0.5rem;
    }
  }

  &::v-deep .v-text--body {
    font-size: inherit;
  }
}
</style>
